import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Building2, Users, Layers, Target, BarChart3, Shield, UserCheck, Activity, LogOut, ChevronRight, Sprout, Globe, FileSearch, Handshake } from "lucide-react";

const NAV_ITEMS = [
    { path: "/admin", icon: Building2, label: "Workspaces", exact: true },
    { path: "/admin/partners", icon: Handshake, label: "Partners" },
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/modules", icon: Layers, label: "Modules" },
    { path: "/admin/archetypes", icon: Target, label: "Archetypes" },
    { path: "/admin/jd-normalizer", icon: FileSearch, label: "JD Normalizer" },
    { path: "/admin/scoring", icon: BarChart3, label: "Scoring" },
    { path: "/admin/equip", icon: Sprout, label: "Equip" },
    { path: "/admin/platform", icon: Globe, label: "Platform" },
    { path: "/admin/audit", icon: Shield, label: "Audit" },
    { path: "/admin/impersonations", icon: UserCheck, label: "View as User Audit" },
    { path: "/admin/health", icon: Activity, label: "System Health" },
];

export function AdminSidebar() {
    const location = useLocation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
    };

    return (
        <aside className="w-56 h-screen bg-card border-r border-border/40 flex flex-col fixed left-0 top-0 z-40" data-testid="admin-sidebar">
            <div className="p-5 border-b border-border/40">
                <Link to="/admin" className="flex items-center gap-2">
                    <span className="text-lg font-display font-bold text-white tracking-tight">Realloc</span>
                    <span className="text-xs text-gray-600 font-mono px-1.5 py-0.5 border border-white/10">ADMIN</span>
                </Link>
            </div>

            <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
                {NAV_ITEMS.map((item) => (
                    <Link key={item.path} to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-200 group ${
                            isActive(item) ? "text-white bg-white/[0.04] border-l-2 border-white" : "text-gray-500 hover:text-white hover:bg-white/[0.02] border-l-2 border-transparent"
                        }`}>
                        <item.icon className={`w-4 h-4 ${isActive(item) ? "text-white" : "text-gray-600 group-hover:text-gray-400"}`} />
                        <span className="font-body">{item.label}</span>
                    </Link>
                ))}
            </nav>

            <div className="p-4 border-t border-border/40">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 bg-white/[0.04] border border-white/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-400">{user?.full_name?.charAt(0) || "A"}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{user?.full_name}</p>
                        <p className="text-xs text-gray-600 truncate">{user?.role?.replace("_", " ")}</p>
                    </div>
                </div>
                <button onClick={() => { logout(); navigate("/login"); }}
                    className="flex items-center gap-2 text-xs text-gray-600 hover:text-red-400 transition-colors w-full">
                    <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
            </div>
        </aside>
    );
}

export function AdminLayout() {
    return (
        <div className="min-h-screen bg-background">
            <AdminSidebar />
            <main className="ml-56 min-h-screen">
                <Outlet />
            </main>
        </div>
    );
}

export default function AdminWorkspacesPage() {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const { api } = useAuth();

    useEffect(() => { fetchWorkspaces(); }, []);

    const fetchWorkspaces = async () => {
        try {
            const res = await api.get("/v2/admin/workspaces");
            setWorkspaces(res.data.workspaces);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const [form, setForm] = useState({
        name: "", primary_industry: "", custom_industry_context: "",
        subscription_tier: "standard", initial_admin_name: "",
        initial_admin_email: "", initial_admin_password: "changeme123",
        data_residency: "US",
        values_framework: { name: "Values Framework", dimensions: [], floor_threshold_pct: 50, floor_cap: 59 },
    });

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/v2/admin/workspaces", form);
            setShowCreate(false);
            fetchWorkspaces();
            setForm({ name: "", primary_industry: "", custom_industry_context: "", subscription_tier: "standard", initial_admin_name: "", initial_admin_email: "", initial_admin_password: "changeme123", data_residency: "US", values_framework: { name: "Values Framework", dimensions: [], floor_threshold_pct: 50, floor_cap: 59 } });
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to create workspace");
        }
    };

    return (
        <div className="p-8" data-testid="admin-workspaces">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white">Workspaces</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage enterprise tenants</p>
                </div>
                <button onClick={() => window.location.assign("/admin/workspaces/new")} data-testid="create-workspace-btn"
                    className="px-4 py-2 bg-white text-black text-sm font-semibold hover:bg-gray-300 transition-colors">
                    Provision Workspace
                </button>
            </div>

            {loading ? (
                <p className="text-sm text-gray-600">Loading...</p>
            ) : (
                <div className="space-y-3">
                    {workspaces.map((w, i) => (
                        <motion.div key={w.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-card border border-border/40 p-5 flex items-center justify-between group hover:border-white/10 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-white/[0.04] border border-white/10 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-gray-500" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-white">{w.name}</h3>
                                    <p className="text-xs text-gray-600">{w.primary_industry} &middot; {w.subscription_tier} &middot; {w.user_count} users</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-600 font-mono">{w.created_at?.slice(0, 10)}</span>
                                <Link to={`/admin/workspace/${w.id}`} className="text-xs text-gray-500 hover:text-white transition-colors flex items-center gap-1">
                                    View <ChevronRight className="w-3 h-3" />
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                    {workspaces.length === 0 && <p className="text-sm text-gray-600">No workspaces yet. Create your first one.</p>}
                </div>
            )}

            {/* Create Workspace Modal */}
            {showCreate && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 p-8 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <h2 className="text-lg font-display font-bold text-white mb-6">New Workspace</h2>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Enterprise Name *</label>
                                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none" placeholder="Acme Corp" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 block mb-1">Industry *</label>
                                    <select value={form.primary_industry} onChange={e => setForm({ ...form, primary_industry: e.target.value })} required
                                        className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none">
                                        <option value="">Select...</option>
                                        {["Financial Services", "Technology", "Healthcare", "Manufacturing", "Retail", "Education", "Energy", "Other"].map(i => <option key={i} value={i}>{i}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 block mb-1">Subscription Tier</label>
                                    <select value={form.subscription_tier} onChange={e => setForm({ ...form, subscription_tier: e.target.value })}
                                        className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none">
                                        {["standard", "professional", "enterprise"].map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Industry Context</label>
                                <textarea value={form.custom_industry_context} onChange={e => setForm({ ...form, custom_industry_context: e.target.value })}
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white placeholder-gray-600 resize-none h-16 outline-none" placeholder="Specific context..." />
                            </div>
                            <div className="border-t border-border/40 pt-4">
                                <p className="text-xs text-gray-400 mb-3 font-semibold">Initial Enterprise Admin</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
                                        <input value={form.initial_admin_name} onChange={e => setForm({ ...form, initial_admin_name: e.target.value })} required
                                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 block mb-1">Email *</label>
                                        <input value={form.initial_admin_email} onChange={e => setForm({ ...form, initial_admin_email: e.target.value })} required type="email"
                                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none" />
                                    </div>
                                </div>
                            </div>
                            <div className="border-t border-border/40 pt-4">
                                <p className="text-xs text-gray-400 mb-3 font-semibold">Values Framework</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 block mb-1">Framework Name</label>
                                        <input value={form.values_framework.name} onChange={e => setForm({ ...form, values_framework: { ...form.values_framework, name: e.target.value } })}
                                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none" placeholder="e.g. TRUST, ICARE" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 block mb-1">Data Residency</label>
                                        <select value={form.data_residency} onChange={e => setForm({ ...form, data_residency: e.target.value })}
                                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none">
                                            {["US", "EU", "UK"].map(r => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="submit" className="flex-1 bg-white text-black py-2.5 text-sm font-semibold hover:bg-gray-300 transition-colors">Create Workspace</button>
                                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 border border-border/40 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
